import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClearFilter } from '../../../../../assets/icons/filter-close.svg';
import i18n from '../../../../../i18n';
import { useAppSelector } from '../../../../../redux/hooks';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../../shared-components/button/Button';
import CustomDatePicker from '../../../../../shared-components/custom-date-range/CustomDatePicker';
import Pagination from '../../../../../shared-components/pagination/Pagination';
import SelectField from '../../../../../shared-components/select-field/SelectField';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';

/**
 * WalletHistory component rendering end consumer wallet history transactions.
 * @returns {React.JSX.Element} WalletHistory component.
 */
function WalletHistory() {
  const { t } = useTranslation('endConsumer');
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [walletHistory, setWalletHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionType, setTransactionType] = useState('allTransactions');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalResults, setTotalResults] = useState<number>(0);

  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });

  /**
   * Function to remove filter from the list of selected filters.
   */
  const handleClearFilters = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
    setTransactionType('allTransactions');
    setPage(1);
  };

  /**
   * Fetch wallet history with filters and pagination.
   * @param {string} userCustomId - The user custom ID.
   * @param {any} [paginationParameters] - Optional page and limit parameters.
   */
  const fetchHistory = useCallback(
    async (userCustomId: string, paginationParameters?: { page?: number; limit?: number }) => {
      setLoading(true);
      try {
        const queryPage = paginationParameters?.page || page;
        const queryLimit = paginationParameters?.limit || limit;

        const query: any = {
          page: queryPage,
          limit: queryLimit,
        };

        if (transactionType !== 'allTransactions') {
          query.type = transactionType;
        }

        if (dateRange.startDate && dateRange.endDate) {
          query.startDate = dayjs(dateRange.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
          query.endDate = dayjs(dateRange.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        const response = await endConsumerApi.fetchWalletHistory(userCustomId, query);
        const data = response?.data;

        if (data && !Array.isArray(data)) {
          const results = data.results || [];
          setWalletHistory(results);
          setPage(data.page || queryPage);
          setLimit(data.limit || queryLimit);
          setTotalPages(data.totalPages || 1);
          setTotalResults(data.totalResults || results.length);
        } else {
          const list = Array.isArray(data) ? data : [];
          setWalletHistory(list);
          setPage(queryPage);
          setLimit(queryLimit);
          setTotalPages(1);
          setTotalResults(list.length);
        }
      } catch (error) {
        console.error('Failed to fetch wallet history:', error);
      } finally {
        setLoading(false);
      }
    },
    [transactionType, dateRange, page, limit],
  );

  useEffect(() => {
    const { userCustomId } = selectedEndConsumer || {};

    if (!userCustomId) return;

    fetchHistory(userCustomId);
  }, [selectedEndConsumer?.userCustomId, dateRange.startDate, dateRange.endDate, transactionType, page]);

  /**
   * Handle transaction filter change.
   * @param {string} _name - Name of the field.
   * @param {any} value - Selected value.
   */
  const handleTransactionFilter = (_name: string, value: any) => {
    setTransactionType(value);
    setPage(1);
  };

  /**
   * Handle date range change.
   * @param {string} name - Name of the field.
   * @param {string} value - Selected value.
   */
  const handleRangeChange = (name: string, value: string) => {
    setDateRange((previous) => ({
      ...previous,
      [name]: value,
    }));
    setPage(1);
  };

  /**
   * Render individual transaction item.
   * @param {any} item - Transaction item object.
   * @returns {React.JSX.Element} Transaction item element.
   */
  const renderTransactionItem = (item: any) => {
    const typeLower = (item.type || '').toLowerCase();
    const isDebit = typeLower === 'debit' || typeLower === 'reserve';
    let stationName =
      item.chargingStationDetails?.name ||
      (isDebit ? t('evChargingSession') : '');

    if (item.type) {
      stationName = `${item.type.toUpperCase()}${stationName ? ` - ${stationName}` : ''}`;
    }

    if (item.rentalSession || item.product === 'powerBank') {
      stationName = t('powerBankRentalSession');
    }
    const time = item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY hh:mm A') : '-';
    const currencyString = (item.currency || 'SAR').toUpperCase();

    return (
      <div key={item.id || item._id} className="wallet-history-item">
        <div className="wallet-history-col-title">
          <p className="label">
            {stationName} <span className="value">{time}</span>
          </p>
        </div>

        <div className="wallet-history-col-txid">
          {item.chargingTransactionId ? (
            <p className="label">
              {t('transactionId')}{' '}
              <span className="value">{item.chargingTransactionId}</span>
            </p>
          ) : (
            <span className="value">-</span>
          )}
        </div>
        <div className="wallet-history-col-amount">
          <p className={`transaction-amount ${isDebit ? 'debited' : 'credited'}`}>
            {isDebit ? '-' : '+'}
            {Number(item.amount).toFixed(2)}{' '}
            {currencyString}
          </p>
        </div>
        <div className="wallet-history-col-balance">
          {item.balanceAfter !== undefined && item.balanceAfter !== null ? (
            <p className="label">
              {t('balanceAfter', { defaultValue: 'Balance' })}{' '}
              <span className="value">
                {Number(item.balanceAfter).toFixed(2)} {currencyString}
              </span>
            </p>
          ) : (
            <span className="value">-</span>
          )}
        </div>


      </div>
    );
  };

  const isGrouped = walletHistory.length > 0 && Array.isArray(walletHistory[0]?.transactions);

  /**
   * Render transaction history list content.
   * @returns {React.JSX.Element | React.JSX.Element[]} Content element.
   */
  const renderContent = () => {
    if (walletHistory.length === 0) {
      return <div className="wallet-history-empty">{t('noTransactionsFound')}</div>;
    }

    if (isGrouped) {
      return walletHistory.map((group) => (
        <div key={group._id?.date || group._id} className="wallet-history-content">
          <p className="wallet-history-date">
            {dayjs(group._id?.date).format(t('dd/mm/yyyy'))}
          </p>
          <div className="wallet-history-item-wrap">
            {group.transactions.map((item: any) => renderTransactionItem(item))}
          </div>
        </div>
      ));
    }

    return (
      <div className="wallet-history-content">
        <div className="wallet-history-item-wrap">
          {walletHistory.map((item: any) => renderTransactionItem(item))}
        </div>
      </div>
    );
  };

  const transactionOptions = [
    { label: t('allTransactions'), value: 'allTransactions' },
    { label: t('credited'), value: 'credit' },
    { label: t('debited'), value: 'debit' },
    { label: t('reserve', { defaultValue: 'Reserve' }), value: 'reserve' },
    { label: t('release', { defaultValue: 'Release' }), value: 'release' },
  ];

  if (loading) {
    return (
      <div className="wallet-history-wrap-loading">
        <BatteryLoader />
      </div>
    );
  }

  /**
   * Handle pagination and limit change.
   * @param {any} newParameters - Pagination parameters object containing page and limit.
   */
  const handlePageLimitChange = (newParameters: any) => {
    const newPage = newParameters.page || page;
    const newLimit = newParameters.limit || limit;
    setPage(newPage);
    setLimit(newLimit);

    if (selectedEndConsumer?.userCustomId) {
      fetchHistory(selectedEndConsumer.userCustomId, { page: newPage, limit: newLimit });
    }
  };

  return (
    <div className="wallet-history-wrap" dir={i18n.dir()}>
      <div className="wallet-history-top">
        <div className="select-field">
          <SelectField
            name="transactionType"
            options={transactionOptions}
            selectedValue={transactionType}
            onChangeValue={handleTransactionFilter}
          />
        </div>

        <div className="wallet-history-date-filter">
          <CustomDatePicker
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleRangeChange}
            placeholder={t('dd/mm/yyyy')}
          />
          <CustomDatePicker
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleRangeChange}
            placeholder={t('dd/mm/yyyy')}
          />
        </div>
        <div className="activity-clear-filter">
          <p> </p>
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={handleClearFilters}
            svgIcon={<ClearFilter />}
            hoverLabel={t('clearFilter')}
          />
        </div>
      </div>

      {renderContent()}

      {totalResults > 0 && (
        <Pagination
          active={page}
          size={totalPages}
          limit={limit}
          totalResults={totalResults}
          loading={loading}
          setActive={handlePageLimitChange}
        />
      )}
    </div>
  );
}

export default WalletHistory;